/**
 * The clients list as a data grid — the same engine the pipeline uses.
 *
 * The columns match the team's working sheet, in their order: S.No, the client
 * name, email, when they registered and from where, the plan, trial end and
 * expiry, cycle, type, promo, payment status, status, who handles them and the
 * contact person. The first two (S.No and the name) are frozen; the rest scroll
 * sideways. Every column filters, the toolbar searches them all, and the set of
 * columns, their order, widths and freeze are saved per user. A row opens the
 * client's full page.
 */
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import { formatDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import {
  Dash, PlanPill, StatusPill, PayPill, TypePill, OwnerPill, EmptyState
} from './Primitives.jsx';
import DataGrid from './grid/DataGrid.jsx';
import useGridPrefs from './grid/useGridPrefs.js';
import { exportAllowed } from './grid/exportGrid.js';

/* Everything shown by default, in the sheet's order; S.No and the name frozen. */
const LAYOUT = { hidden: [], pageSize: 25, frozen: 2 };

/** One grid row: the client and the derived facts its cells read, once per row. */
function clientRow(derive, r) {
  const d = derive(r);
  return {
    r,
    d,
    reg: d.reg || null,
    trial: d.noTrial ? null : (d.trial || null),
    noTrial: !!d.noTrial,
    exp: d.exp || null,
    type: d.type || r.acctType || ''
  };
}

function clientColumns({ S, dateFmt }) {
  const day = v => formatDate(v, false, dateFmt);
  return [
    {
      id: 'sno',
      size: 64,
      enableSorting: false,
      accessorFn: r => r.r.sno,
      meta: { label: 'S.No', filter: false, search: false, align: 'center' },
      cell: ({ getValue }) => <span className="dim">{getValue() ?? ''}</span>
    },
    {
      id: 'name',
      size: 240,
      minSize: 160,
      accessorFn: r => r.r.name || undefined,
      meta: { label: 'Client', type: 'text' },
      cell: ({ row: { original: { r } } }) => <b title={r.name}>{r.name || 'Untitled'}</b>
    },
    {
      id: 'email',
      size: 230,
      accessorFn: r => r.r.email || undefined,
      meta: { label: 'Email', type: 'text' },
      cell: ({ getValue }) => (getValue()
        ? <a href={`mailto:${getValue()}`} onClick={e => e.stopPropagation()}>{getValue()}</a>
        : <Dash />)
    },
    {
      id: 'regOn',
      size: 160,
      accessorFn: r => r.reg || undefined,
      meta: { label: 'Registered on', type: 'date' },
      cell: ({ getValue }) => (getValue() ? day(getValue()) : <Dash />)
    },
    {
      id: 'source',
      size: 150,
      accessorFn: r => r.r.source || undefined,
      meta: { label: 'Reg. source', type: 'enum' },
      cell: ({ getValue }) => getValue() || <Dash />
    },
    {
      id: 'plan',
      size: 130,
      accessorFn: r => r.r.plan || undefined,
      meta: { label: 'Current plan', type: 'enum', options: S.plans },
      cell: ({ getValue }) => (getValue() ? <PlanPill value={getValue()} /> : <Dash />)
    },
    {
      id: 'trialEnd',
      size: 150,
      accessorFn: r => r.trial || undefined,
      meta: { label: 'Trial end', type: 'date', blank: 'No trial' },
      cell: ({ row: { original: r } }) => (r.noTrial
        ? <span className="dim">No trial</span>
        : r.trial ? day(r.trial) : <Dash />)
    },
    {
      id: 'expiry',
      size: 150,
      accessorFn: r => r.exp || undefined,
      meta: { label: 'Expiry date', type: 'date' },
      cell: ({ getValue }) => (getValue() ? day(getValue()) : <Dash />)
    },
    {
      id: 'cycle',
      size: 120,
      accessorFn: r => r.r.cycle || undefined,
      meta: { label: 'Cycle', type: 'enum', options: S.cycles }
    },
    {
      id: 'type',
      size: 120,
      accessorFn: r => r.type || undefined,
      meta: { label: 'Type', type: 'enum', options: S.types },
      cell: ({ getValue }) => (getValue() ? <TypePill value={getValue()} /> : <Dash />)
    },
    {
      id: 'promo',
      size: 140,
      accessorFn: r => r.r.promo || undefined,
      meta: { label: 'Promo code', type: 'text' }
    },
    {
      id: 'pay',
      size: 140,
      accessorFn: r => r.r.pay || undefined,
      meta: { label: 'Payment status', type: 'enum', options: S.payments },
      cell: ({ getValue }) => (getValue() ? <PayPill value={getValue()} paidValues={S.paidValues} /> : <Dash />)
    },
    {
      id: 'status',
      size: 130,
      accessorFn: r => r.r.status || undefined,
      meta: { label: 'Status', type: 'enum', options: S.statuses },
      cell: ({ getValue }) => (getValue() ? <StatusPill value={getValue()} /> : <Dash />)
    },
    {
      id: 'handler',
      size: 150,
      accessorFn: r => r.r.handler || undefined,
      meta: { label: 'Handled by', type: 'enum', blank: 'Unowned' },
      cell: ({ getValue }) => <OwnerPill name={getValue()} />
    },
    {
      id: 'contact',
      size: 180,
      accessorFn: r => r.r.contact || undefined,
      meta: { label: 'Contact person', type: 'text' }
    }
  ];
}

export default function ClientsGrid({ list, view }) {
  const { S, derive, fmtDate, me, menus } = useApp();
  const navigate = useNavigate();
  const layout = useGridPrefs(`grid:clients:${view || 'all'}`, LAYOUT);

  const data = useMemo(() => list.map(r => clientRow(derive, r)), [list, derive]);

  const columns = useMemo(
    () => clientColumns({ S, dateFmt: S.dateFmt }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [S.dateFmt, (S.plans || []).join('|'), (S.statuses || []).join('|'), (S.types || []).join('|')]
  );

  const open = useRef(null);
  open.current = r => navigate(`/clients/${r.r.id}`);

  return (
    <DataGrid
      prefs={layout}
      columns={columns}
      data={data}
      getRowId={r => r.r.id}
      onRowClick={r => open.current(r)}
      label="Clients"
      exportName="xyra-clients"
      exportSheet="Clients"
      canExport={exportAllowed(me, menus)}
      dateFmt={S.dateFmt}
      emptyState={(
        <EmptyState icon="users" tone="g-cyan" title="No clients match">
          Clear the filters to see the whole book.
        </EmptyState>
      )}
    />
  );
}
